import { useMemo } from 'react';
import { StyleSheet, TextStyle, ViewStyle, Platform } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { createStyles, createCommonStyles, addColorOpacity } from '../shared/utils/styles';
import { DesignTokens } from '../shared/types/tokens';

/**
 * Custom hook that provides theme-aware styles
 * This eliminates the need for makeStyles functions in every component
 */
export function useStyles() {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const commonStyles = createCommonStyles(tokens);

  return useMemo(() => ({
    // Theme tokens and utilities
    tokens,
    utils,
    
    // Common reusable styles
    ...commonStyles,
    
    // Helper functions for common patterns
    text: (size: keyof typeof utils.typography.sizes = 'base'): TextStyle => ({
      color: tokens.color.onBackground,
      fontSize: utils.typography.sizes[size],
      fontFamily: utils.typography.body.fontFamily,
    }),
    
    heading: (size: keyof typeof utils.typography.sizes = 'xl'): TextStyle => ({
      color: tokens.color.onBackground,
      fontSize: utils.typography.sizes[size],
      fontWeight: utils.typography.weights.bold,
      fontFamily: utils.typography.heading.fontFamily,
    }),
    
    // Common layout patterns
    // NOTE: Use PageContainer component instead of pageContainer style
    // This is kept for backward compatibility only
    pageContainer: {
      flex: 1,
      backgroundColor: tokens.color.background,
      paddingHorizontal: utils.spacing.lg,
    } as ViewStyle,
    
    // Layout utilities - use with PageContainer
    getContainerMaxWidth: () => {
      // This will be handled by PageContainer, but kept for reference
      return { mobile: 500, tablet: 600, desktop: 700, largeDesktop: 800 };
    },
    
    getContainerPadding: () => {
      // This will be handled by PageContainer, but kept for reference
      return {
        mobile: utils.spacing.md,
        tablet: utils.spacing.lg,
        desktop: utils.spacing.xl,
        largeDesktop: utils.spacing.xl,
      };
    },
    
    section: {
      marginBottom: utils.spacing.xl,
    } as ViewStyle,
    
    sectionTitle: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
    } as TextStyle,
    
    // Modal patterns
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
    } as ViewStyle,
    
    modalContent: {
      backgroundColor: tokens.color.background,
      borderRadius: utils.radius.lg,
      padding: utils.spacing.lg,
      width: '90%',
      maxWidth: 500,
      maxHeight: '90%',
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      }),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 16,
      elevation: 4,
    } as ViewStyle,
    
    modalTitle: {
      fontSize: utils.typography.sizes['2xl'],
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.lg,
      fontFamily: utils.typography.heading.fontFamily,
    } as TextStyle,
    
    // Form patterns
    form: {
      width: '100%',
      maxWidth: 400,
      marginBottom: utils.spacing.md,
    } as ViewStyle,
    
    formInput: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
      marginBottom: utils.spacing.md,
    } as TextStyle,
    
    // Button patterns
    buttonRow: {
      flexDirection: 'row',
      gap: utils.spacing.md,
    } as ViewStyle,
    
    // Card patterns
    card: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      padding: utils.spacing.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
      marginBottom: utils.spacing.md,
    } as ViewStyle,
    
    // Empty state
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: utils.spacing['2xl'],
    } as ViewStyle,
    
    emptyText: {
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onBackground, 0.8),
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
    } as TextStyle,
    
    // Loading
    loadingText: {
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onBackground, 0.8),
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
    } as TextStyle,
    
    // Modern card shadow (for inline use)
    cardShadow: Platform.select({
      web: {
        boxShadow: `0 4px 12px ${addColorOpacity('#000', 0.08)}, 0 1px 3px ${addColorOpacity('#000', 0.04)}`,
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 12,
        elevation: 4,
      },
    }) as ViewStyle,
    
    // Subtle card shadow
    cardShadowSubtle: Platform.select({
      web: {
        boxShadow: `0 2px 8px ${addColorOpacity('#000', 0.06)}`,
      } as any,
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
      },
    }) as ViewStyle,
    
    // Glass effect (for accent cards)
    glassEffect: {
      backgroundColor: addColorOpacity(tokens.color.surface, 0.8),
      backdropFilter: 'blur(10px)',
      borderWidth: 1,
      borderColor: addColorOpacity(tokens.color.border, 0.5),
    } as ViewStyle,
    
    // Gradient background helper
    accentGradient: {
      backgroundColor: tokens.color.primary,
    } as ViewStyle,
    
  }), [tokens, utils, commonStyles]);
}

